
import { supabase } from "@/integrations/supabase/client";

/**
 * Add an email to the marketing contacts list and sync with Brevo
 * @param email - Email address
 * @param name - Name (optional)
 * @param source - Source of the contact (e.g., 'newsletter', 'contact_form')
 * @returns Promise with operation result
 */
export const addToMarketingList = async (
  email: string,
  name: string = '',
  source: string = 'website',
  marketingConsent: boolean = true
): Promise<{ success: boolean; error?: any }> => {
  try {
    // Ensure name is always provided - if empty, use the email prefix
    const safeName = name || email.split('@')[0];
    console.log(`Adding to marketing list: ${email}, name: ${safeName}, source: ${source}`);
    
    // First check if the email already exists using a custom query
    // since we can't directly access the tables via the type system
    const { data: checkResult, error: checkError } = await supabase
      .rpc('check_marketing_contact_exists', { p_email: email });

    if (checkError) throw checkError;

    if (checkResult) {
      // Email already exists, update it
      const { error: updateError } = await supabase
        .rpc('update_marketing_contact', { 
          p_email: email,
          p_name: safeName,
          p_marketing_consent: marketingConsent
        });

      if (updateError) throw updateError;
    } else {
      // Email doesn't exist, insert a new record
      const { error: insertError } = await supabase
        .rpc('insert_marketing_contact', {
          p_email: email,
          p_name: safeName,
          p_marketing_consent: marketingConsent,
          p_source: source
        });

      if (insertError) throw insertError;
    }

    // If marketing consent is given, sync with Brevo API
    if (marketingConsent) {
      try {
        // Log the exact parameters we're passing to Brevo for debugging
        console.log(`Syncing with Brevo: ${email}, name: ${safeName}`);
        
        // Pass the safeName to ensure it's never empty
        const brevoResult = await syncContactWithBrevo(email, safeName);
        console.log(`Successfully synced ${email} with Brevo:`, brevoResult);
        
        // The welcome email series is now handled through localStorage scheduling to ensure
        // the first email is sent the next morning (at least 12h after subscription)
        // We'll store the scheduled time in localStorage and check it periodically.
        console.log(`Welcome email series will be scheduled through localStorage timing`);
      } catch (brevoError) {
        console.error('Error syncing with Brevo:', brevoError);
        // Continue execution even if Brevo sync fails
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error adding email to marketing list:', error);
    return { success: false, error };
  }
};

/**
 * Remove an email from the marketing contacts list
 * @param email - Email address
 * @returns Promise with operation result
 */
export const removeFromMarketingList = async (
  email: string
): Promise<{ success: boolean; error?: any }> => {
  try {
    console.log(`Attempting to remove ${email} from marketing list`);
    
    // Call the unsubscribe edge function
    const { data, error } = await supabase.functions.invoke('unsubscribe-from-marketing', {
      body: { email }
    });

    if (error) {
      console.error('Edge function error:', error);
      throw error;
    }
    
    if (!data.success) {
      console.error('Unsubscribe operation failed:', data.error);
      throw new Error(data.error || 'Unknown error occurred');
    }
    
    console.log(`Successfully removed ${email} from marketing list`);
    return { success: true };
  } catch (error) {
    console.error('Error removing email from marketing list:', error);
    return { success: false, error };
  }
};

/**
 * Generate an unsubscribe URL for the given email
 * @param email - Email address
 * @returns Unsubscribe URL
 */
export const getUnsubscribeUrl = (email: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/unsubscribe?email=${encodeURIComponent(email)}`;
};

/**
 * Sync a contact with Brevo marketing platform
 */
const syncContactWithBrevo = async (email: string, name: string = '') => {
  // Ensure name is always provided - if empty, use the email prefix
  const safeName = name || email.split('@')[0];
  console.log(`Syncing with Brevo: ${email}, name: ${safeName}`);
  
  // Create the edge function that will use the BREVO_API_KEY secret
  const { data, error } = await supabase.functions.invoke('sync-with-brevo', {
    body: { email, name: safeName }
  });

  if (error) {
    console.error('Error invoking sync-with-brevo function:', error);
    throw error;
  }
  return data;
};

/**
 * Schedule a follow-up email to be sent to customers who completed a Sambla application
 * Invites them to try other loan partners through Axo
 * @param email - Customer email address
 * @param name - Customer name
 * @param token - Application token for tracking
 */
export const schedulePartnerFollowupEmail = async (
  email: string, 
  name: string = '', 
  token: string = ''
): Promise<{ success: boolean; error?: any }> => {
  try {
    console.log(`Scheduling partner follow-up email for ${email}`);
    
    // Store the scheduled date in localStorage - in a production app this would be server-side
    const scheduledTime = new Date();
    scheduledTime.setHours(scheduledTime.getHours() + 3); // Send email 3 hours later
    
    localStorage.setItem(`partner_followup_${email}`, JSON.stringify({
      email,
      name,
      token,
      scheduledDate: scheduledTime.toISOString(),
      sent: false
    }));
    
    // In a real app, you would set up a server-side scheduled task
    // For demo purposes, we'll create a function that checks if it's time to send
    console.log(`Partner follow-up email for ${email} scheduled for ${scheduledTime.toISOString()}`);
    
    return { success: true };
  } catch (error) {
    console.error('Error scheduling partner follow-up email:', error);
    return { success: false, error };
  }
};

/**
 * Send the partner follow-up email if it's time
 * This would normally be triggered by a server-side scheduler
 */
export const sendPartnerFollowupEmail = async (email: string): Promise<{ success: boolean; error?: any }> => {
  try {
    const storedDataStr = localStorage.getItem(`partner_followup_${email}`);
    if (!storedDataStr) {
      return { success: false, error: 'No scheduled follow-up found' };
    }
    
    const storedData = JSON.parse(storedDataStr);
    
    if (storedData.sent) {
      return { success: false, error: 'Email already sent' };
    }
    
    const scheduledDate = new Date(storedData.scheduledDate);
    if (scheduledDate > new Date()) {
      return { success: false, error: 'Not time to send yet' };
    }
    
    // Send the email through our edge function
    const { data: responseData, error } = await supabase.functions.invoke('partner-followup-email', {
      body: {
        email: storedData.email,
        name: storedData.name,
        token: storedData.token
      }
    });
    
    if (error) throw error;
    
    // Mark as sent
    storedData.sent = true;
    localStorage.setItem(`partner_followup_${email}`, JSON.stringify(storedData));
    
    console.log(`Partner follow-up email sent successfully to ${email}`);
    return { success: true };
  } catch (error) {
    console.error('Error sending partner follow-up email:', error);
    return { success: false, error };
  }
};

/**
 * Check and send all scheduled partner follow-up emails that are due
 * This could be called periodically from the application
 */
export const processScheduledFollowupEmails = async (): Promise<{ success: boolean; sent: number; error?: any }> => {
  try {
    let sentCount = 0;
    
    // Get all keys from localStorage that match our pattern
    const keys = Object.keys(localStorage).filter(key => key.startsWith('partner_followup_'));
    
    for (const key of keys) {
      const storedDataStr = localStorage.getItem(key);
      if (!storedDataStr) continue;
      
      const storedData = JSON.parse(storedDataStr);
      if (storedData.sent) continue;
      
      const scheduledDate = new Date(storedData.scheduledDate);
      if (scheduledDate <= new Date()) {
        // Time to send this email
        const result = await sendPartnerFollowupEmail(storedData.email);
        if (result.success) {
          sentCount++;
        }
      }
    }
    
    return { success: true, sent: sentCount };
  } catch (error) {
    console.error('Error processing scheduled follow-up emails:', error);
    return { success: false, sent: 0, error };
  }
};

/**
 * Start the welcome email series by sending the first email
 * This is now called when the scheduled time has been reached
 */
export const startWelcomeEmailSeries = async (email: string, name: string = '') => {
  // Ensure name is always provided - if empty, use the email prefix
  const safeName = name || email.split('@')[0];
  console.log(`Starting welcome email series for ${email}, name: ${safeName}`);
  
  // Send the first email in the welcome series
  const { data, error } = await supabase.functions.invoke('welcome-email-series', {
    body: { 
      email, 
      name: safeName,
      emailIndex: 0 // Start with the first email
    }
  });

  if (error) {
    console.error('Error invoking welcome-email-series function:', error);
    throw error;
  }
  
  if (data.nextEmailIndex !== undefined) {
    // Store the next email information in localStorage for scheduling logic
    // In a production app, this would be handled server-side with proper scheduling
    const nextEmailDate = new Date();
    nextEmailDate.setDate(nextEmailDate.getDate() + 3); // Schedule 3 days after the first email
    
    localStorage.setItem(`welcome_email_next_${email}`, JSON.stringify({
      nextIndex: data.nextEmailIndex,
      scheduledDate: nextEmailDate.toISOString(),
      name: safeName
    }));
  }
  
  return data;
};

/**
 * Send the next email in the welcome series
 * This would normally be triggered by a server-side scheduler
 * For demo purposes, we're implementing client-side logic
 */
export const sendNextWelcomeEmail = async (email: string, name: string = '') => {
  const nextEmailData = localStorage.getItem(`welcome_email_next_${email}`);
  
  if (!nextEmailData) return { success: false, error: 'No scheduled email found' };
  
  const { nextIndex, scheduledDate } = JSON.parse(nextEmailData);
  
  // Check if it's time to send the next email
  if (new Date(scheduledDate) > new Date()) {
    return { success: false, error: 'Next email not due yet' };
  }
  
  // Send the next email in the series
  const { data, error } = await supabase.functions.invoke('welcome-email-series', {
    body: { 
      email, 
      name,
      emailIndex: nextIndex
    }
  });
  
  if (error) throw error;
  
  // Update the next email information if there is another email
  if (data.nextEmailIndex !== undefined) {
    localStorage.setItem(`welcome_email_next_${email}`, JSON.stringify({
      nextIndex: data.nextEmailIndex,
      scheduledDate: new Date(Date.now() + (data.nextEmailDelay * 24 * 60 * 60 * 1000)).toISOString()
    }));
  } else {
    // This was the last email, remove the localStorage item
    localStorage.removeItem(`welcome_email_next_${email}`);
  }
  
  return data;
};

/**
 * Trigger the test welcome email sequence
 * This will send all three welcome emails to the specified email at once
 */
export const sendTestWelcomeEmails = async (email: string = "asiakaspalvelu@lainasampo.fi") => {
  try {
    const { data, error } = await supabase.functions.invoke('test-welcome-emails', {
      body: { email }
    });

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Error sending test welcome emails:', error);
    return { success: false, error };
  }
};
